interface PerformanceMetric {
  id: string;
  timestamp: Date;
  operation: string;
  duration: number;
  success: boolean;
  errorMessage?: string;
  metadata?: Record<string, any>;
}

interface ErrorEvent {
  id: string;
  timestamp: Date;
  source: string;
  error: string;
  context?: Record<string, any>;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private errors: ErrorEvent[] = [];
  private maxMetrics = 1000;
  private maxErrors = 500;

  startTimer(operation: string): (success?: boolean, errorMessage?: string, metadata?: Record<string, any>) => void {
    const startTime = performance.now();
    const id = `${operation}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    return (success: boolean = true, errorMessage?: string, metadata?: Record<string, any>) => {
      const duration = performance.now() - startTime;

      const metric: PerformanceMetric = {
        id,
        timestamp: new Date(),
        operation,
        duration,
        success,
        errorMessage,
        metadata
      };

      this.addMetric(metric);

      if (!success && errorMessage) {
        this.trackError({
          id: `error-${id}`,
          timestamp: new Date(),
          source: operation,
          error: errorMessage,
          context: metadata,
          severity: this.determineSeverity(operation, errorMessage)
        });
      }
    };
  }

  trackError(error: ErrorEvent): void {
    this.errors.unshift(error);

    if (this.errors.length > this.maxErrors) {
      this.errors = this.errors.slice(0, this.maxErrors);
    }
    if (error.severity === 'critical') {
      this.notifyAdmin(error);
    }
  }

  private addMetric(metric: PerformanceMetric): void {
    this.metrics.unshift(metric);

    if (this.metrics.length > this.maxMetrics) {
      this.metrics = this.metrics.slice(0, this.maxMetrics);
    }
  }

  private determineSeverity(operation: string, errorMessage: string): 'low' | 'medium' | 'high' | 'critical' {
    const lowerError = errorMessage.toLowerCase();

    if (lowerError.includes('network') || lowerError.includes('timeout')) {
      return 'medium';
    }

    if (lowerError.includes('authentication') || lowerError.includes('unauthorized')) {
      return 'high';
    }

    if (operation.includes('supabase') && lowerError.includes('connection')) {
      return 'critical';
    }

    return 'low';
  }

  private async notifyAdmin(error: ErrorEvent): Promise<void> {
    try {
      await fetch('/api/notify/error', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'critical_error',
          error: error.error,
          source: error.source,
          timestamp: error.timestamp,
          context: error.context
        })
      });
    } catch (notificationError) {
    }
  }

  getMetrics(limit: number = 100): PerformanceMetric[] {
    return this.metrics.slice(0, limit);
  }

  getErrors(limit: number = 50): ErrorEvent[] {
    return this.errors.slice(0, limit);
  }

  getPerformanceSummary(timeRange: number = 3600000): {
    totalOperations: number;
    successRate: number;
    averageResponseTime: number;
    slowestOperations: PerformanceMetric[];
    recentErrors: ErrorEvent[];
  } {
    const cutoff = new Date(Date.now() - timeRange);
    const recentMetrics = this.metrics.filter(m => m.timestamp >= cutoff);
    const recentErrors = this.errors.filter(e => e.timestamp >= cutoff);

    const totalOperations = recentMetrics.length;
    const successfulOperations = recentMetrics.filter(m => m.success).length;
    const successRate = totalOperations > 0 ? (successfulOperations / totalOperations) * 100 : 100;

    const durations = recentMetrics.map(m => m.duration);
    const averageResponseTime = durations.length > 0
      ? durations.reduce((sum, duration) => sum + duration, 0) / durations.length
      : 0;

    const slowestOperations = recentMetrics
      .sort((a, b) => b.duration - a.duration)
      .slice(0, 5);

    return {
      totalOperations,
      successRate,
      averageResponseTime,
      slowestOperations,
      recentErrors: recentErrors.slice(0, 10)
    };
  }

  clearOldData(maxAge: number = 86400000): void {
    const cutoff = new Date(Date.now() - maxAge);
    this.metrics = this.metrics.filter(m => m.timestamp >= cutoff);
    this.errors = this.errors.filter(e => e.timestamp >= cutoff);
  }

  exportData(): { metrics: PerformanceMetric[]; errors: ErrorEvent[] } {
    return {
      metrics: [...this.metrics],
      errors: [...this.errors]
    };
  }
}

export const performanceMonitor = new PerformanceMonitor();

export const withPerformanceTracking = async <T>(
  operation: string,
  fn: () => Promise<T>,
  metadata?: Record<string, any>
): Promise<T> => {
  const stopTimer = performanceMonitor.startTimer(operation);

  try {
    const result = await fn();
    stopTimer(true, undefined, metadata);
    return result;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    stopTimer(false, errorMessage, metadata);
    throw error;
  }
};

export const trackAsyncOperation = <T extends any[], R>(
  operation: string,
  fn: (...args: T) => Promise<R>
) => {
  return async (...args: T): Promise<R> => {
    return withPerformanceTracking(operation, () => fn(...args));
  };
};

export type { PerformanceMetric, ErrorEvent };